import { BooMark } from "@/components/boo/BooMark";
import { ROLE_LABEL, personName } from "@/components/boo/access-copy";
import { Many } from "@/components/grants/Many";
import { OverrideReason, REASON_MIN, SheetShell } from "@/components/grants/SheetShell";
import { InlineError } from "@/components/QueryState";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { api, type BooRole, type HandoverPreview, type PersonRef } from "@/lib/api";
import { num, personKey } from "@/lib/grant-scopes";
import { cn } from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, Check, Link2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

// Handing a Boo over when the person it serves leaves or changes role: the
// same Boo gets a new owner and keeps everything it learned, its skills and
// its links, so nobody has to teach it again. What it learned in private
// 1-on-1s with the person leaving can be archived on the way. A Boo with no
// owner gets one the same way, worded as assigning an owner.

type Fate = BooRole | "remove";
const STEPS = ["People", "Private conversations", "Review"] as const;

export function HandoverSheet({ booId, onClose }: { booId: string; onClose: () => void }) {
  const preview = useQuery({ queryKey: ["handover", booId], queryFn: () => api.access.handoverPreview(booId), staleTime: 0 });
  return preview.isLoading || !preview.data ? (
    <SheetShell title="Hand over a Boo" onClose={onClose}>
      {preview.isError ? <InlineError error={preview.error} /> : <Skeleton className="h-48 w-full rounded-lg" />}
    </SheetShell>
  ) : (
    <HandoverWizard preview={preview.data} onClose={onClose} />
  );
}

function HandoverWizard({ preview, onClose }: { preview: HandoverPreview; onClose: () => void }) {
  const qc = useQueryClient();
  const boo = preview.boo;
  const [step, setStep] = useState(0);
  const [toUserId, setToUserId] = useState("");
  const holders = preview.roles.filter((r) => r.role === "owner" || r.role === "admin");
  const [fates, setFates] = useState<Record<string, Fate>>(() =>
    Object.fromEntries(holders.map((r) => [r.userId, r.role === "owner" || !r.inOrg ? "remove" : r.role])),
  );
  // Null follows the suggestion until someone ticks a box.
  const [picked, setPicked] = useState<Set<string> | null>(null);
  const [rename, setRename] = useState(boo.name);
  const [reason, setReason] = useState("");
  const org = preview.via === "org_owner";
  const ownerless = !preview.roles.some((r) => r.role === "owner");

  const others = holders.filter((r) => r.userId !== toUserId);
  const leaving = others.filter((r) => fates[r.userId] === "remove");
  // Pre-ticks only people whose chat name is exactly the full name of
  // someone leaving: dashboard users and chat identities aren't linked, and
  // a loose match would archive a bystander's 1-on-1s.
  const suggested = (() => {
    const norm = (n: string) => n.trim().replace(/\s+/g, " ").toLowerCase();
    const names = new Set(leaving.map((r) => r.name).filter((n): n is string => !!n && n.trim().includes(" ")).map(norm));
    return new Set((preview.subjects ?? []).filter((s) => s.name && names.has(norm(s.name))).map((s) => personKey(s)));
  })();
  const archive = picked ?? suggested;

  const newOwner = preview.candidates.find((c) => c.userId === toUserId);
  const archiveRefs: Array<PersonRef & { name: string | null; count: number }> = (preview.subjects ?? []).filter((s) => archive.has(personKey(s)));
  const reasonOk = !org || reason.trim().length >= REASON_MIN;

  const go = useMutation({
    mutationFn: () =>
      api.access.handover(boo.id, {
        toUserId,
        others: others.map((r) => ({ userId: r.userId, role: fates[r.userId] ?? r.role })),
        rename: rename.trim() && rename.trim() !== boo.name ? rename.trim() : null,
        archive: archiveRefs.map((s) => ({ provider: s.provider, providerUserId: s.providerUserId })),
        reason: reason.trim() || null,
      }),
    onSuccess: () => {
      toast.success(`${rename.trim() || boo.name} now belongs to ${personName(newOwner)}.`);
      for (const key of [["containers"], ["boo-members", boo.id], ["boo-access-events", boo.id], ["org-access"], ["org-access-events"], ["handover", boo.id]]) {
        void qc.invalidateQueries({ queryKey: key });
      }
      onClose();
    },
  });

  const canNext = step === 0 ? !!toUserId : step === 1 ? true : reasonOk && !!toUserId;

  return (
    <SheetShell
      title={ownerless ? `Assign an owner to ${boo.name}` : `Hand over ${boo.name}`}
      description={
        ownerless
          ? `${boo.name} has no owner. It keeps everything it learned, its skills and its links. You can rename it and archive private one-on-ones on the way.`
          : "Same Boo, new owner. It keeps everything it learned, its skills and its links. You can rename it and archive private one-on-ones on the way."
      }
      onClose={onClose}
      busy={go.isPending}
      footer={
        <>
          {step > 0 && (
            <Button variant="ghost" className="mr-auto" onClick={() => setStep(step - 1)} disabled={go.isPending}>
              Back
            </Button>
          )}
          <Button variant="outline" onClick={onClose} disabled={go.isPending}>
            Cancel
          </Button>
          {step < STEPS.length - 1 ? (
            <Button onClick={() => setStep(step + 1)} disabled={!canNext}>
              Continue
            </Button>
          ) : (
            <Button onClick={() => go.mutate()} loading={go.isPending} disabled={!canNext}>
              {ownerless ? "Assign owner" : "Hand over"}
            </Button>
          )}
        </>
      }
    >
      <ol className="flex items-center gap-2 text-xs" aria-label="Steps">
        {STEPS.map((s, i) => (
          <li key={s} className="flex items-center gap-2">
            <span
              className={cn(
                "flex size-5 items-center justify-center rounded-full border text-[11px] tabular-nums",
                i < step ? "border-foreground bg-foreground text-background" : i === step ? "border-foreground" : "border-border text-muted-foreground",
              )}
            >
              {i < step ? <Check className="size-3" /> : i + 1}
            </span>
            <span className={cn(i === step ? "font-medium" : "text-muted-foreground")}>{s}</span>
            {i < STEPS.length - 1 && <span className="h-px w-6 bg-border" aria-hidden />}
          </li>
        ))}
      </ol>

      {step === 0 && (
        <>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new-owner">New owner</Label>
            <Select value={toUserId} onValueChange={setToUserId}>
              <SelectTrigger id="new-owner" className="w-full">
                <SelectValue placeholder="Choose who takes over" />
              </SelectTrigger>
              <SelectContent>
                {preview.candidates.map((c) => (
                  <SelectItem key={c.userId} value={c.userId}>
                    {personName(c)}
                    {c.email && c.name ? <span className="text-muted-foreground"> · {c.email}</span> : null}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {others.length > 0 && (
            <div className="flex flex-col gap-2">
              <Label>Everyone who manages it now</Label>
              <div className="flex flex-col divide-y divide-border rounded-lg border border-border">
                {others.map((r) => (
                  <div key={r.userId} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 px-4 py-2.5">
                    <span className="min-w-0">
                      <span className="block text-sm [overflow-wrap:anywhere]">{personName(r, "Someone who left")}</span>
                      <span className="block text-xs text-muted-foreground">
                        {ROLE_LABEL[r.role]}
                        {!r.inOrg && " · no longer in your organization"}
                      </span>
                    </span>
                    <Select value={fates[r.userId]} onValueChange={(v) => setFates({ ...fates, [r.userId]: v as Fate })} disabled={!r.inOrg}>
                      <SelectTrigger className="w-40" aria-label={`What happens to ${personName(r)}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(["owner", "admin", "member"] as const).map((role) => (
                          <SelectItem key={role} value={role}>
                            {role === r.role ? `Keep as ${ROLE_LABEL[role].toLowerCase()}` : `Make ${ROLE_LABEL[role].toLowerCase()}`}
                          </SelectItem>
                        ))}
                        <SelectItem value="remove">Remove access</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {step === 1 && (
        <div className="flex flex-col gap-3">
          <p className="text-sm text-muted-foreground" title="Archived memories are kept for the record but no longer used. Channel and organization memories stay.">
            Archive private one-on-ones with (optional):
          </p>
          {preview.subjects === null ? (
            <p className="rounded-md bg-muted/60 px-3 py-2 text-sm text-muted-foreground">
              {boo.running
                ? `${boo.name} can't list its one-on-one conversations yet. You can hand it over now and archive later.`
                : `${boo.name} isn't running, so its one-on-one conversations can't be archived now. Start it first, or hand it over without archiving.`}
            </p>
          ) : preview.subjects.length === 0 ? (
            <p className="text-sm text-muted-foreground">No one-on-ones.</p>
          ) : (
            <div className="flex flex-col divide-y divide-border rounded-lg border border-border">
              <Many
                items={[...preview.subjects].sort((a, b) => Number(suggested.has(personKey(b))) - Number(suggested.has(personKey(a))))}
                noun="people"
                text={(s) => `${s.name ?? ""} ${s.providerUserId}`}
                render={(s) => {
                  const key = personKey(s);
                  return (
                    <label key={key} className="flex cursor-pointer items-start gap-3 px-4 py-2.5 text-sm hover:bg-muted/30">
                      <Checkbox
                        className="mt-0.5"
                        checked={archive.has(key)}
                        onCheckedChange={(v) => {
                          const next = new Set(archive);
                          if (v === true) next.add(key);
                          else next.delete(key);
                          setPicked(next);
                        }}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block [overflow-wrap:anywhere]">
                          {s.name ?? "Someone without a name on file"}
                          {!s.name && <span className="ml-1.5 font-mono text-[11px] text-muted-foreground">{s.providerUserId}</span>}
                        </span>
                        {suggested.has(key) && <span className="block text-xs text-muted-foreground">Same name as someone leaving</span>}
                      </span>
                      <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                        {num(s.count)} {s.count === 1 ? "memory" : "memories"}
                      </span>
                    </label>
                  );
                }}
              />
            </div>
          )}
        </div>
      )}

      {step === 2 && (
        <>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="handover-name">Name</Label>
            <Input id="handover-name" value={rename} maxLength={80} onChange={(e) => setRename(e.target.value)} />
            <p className="text-xs text-muted-foreground">Optional. Change it to rename the Boo.</p>
          </div>
          <div className="flex flex-col gap-2 rounded-lg border border-border p-4 text-sm">
            <div className="flex items-center gap-3">
              <BooMark id={boo.id} className="size-8" />
              <span className="font-medium">{rename.trim() || boo.name}</span>
            </div>
            <Summary icon={Check}>{personName(newOwner)} becomes its owner.</Summary>
            {others.map((r) => (
              <Summary key={r.userId} icon={Check}>
                {personName(r, "Someone who left")}{" "}
                {fates[r.userId] === "remove" ? "loses access." : fates[r.userId] === r.role ? `stays ${ROLE_LABEL[r.role].toLowerCase()}.` : `becomes ${ROLE_LABEL[fates[r.userId] as BooRole].toLowerCase()}.`}
              </Summary>
            ))}
            <Summary icon={Archive}>
              {archiveRefs.length
                ? `Archives private conversations with ${archiveRefs.map((s) => s.name ?? s.providerUserId).join(", ")}.`
                : "Archives nothing."}
            </Summary>
            <Summary icon={Link2}>
              Keeps learning from {preview.links.using} {preview.links.using === 1 ? "Boo" : "Boos"} and sharing with {preview.links.sharing}.
            </Summary>
          </div>
          {org ? (
            <OverrideReason value={reason} onChange={setReason} boo={boo.name} />
          ) : (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="handover-reason" className="text-xs text-muted-foreground">
                Note for the access history (optional)
              </Label>
              <Textarea id="handover-reason" rows={2} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} />
            </div>
          )}
          {go.isError && <InlineError error={go.error} />}
        </>
      )}
    </SheetShell>
  );
}

function Summary({ icon: Icon, children }: { icon: typeof Check; children: React.ReactNode }) {
  return (
    <p className="flex items-start gap-2 text-muted-foreground">
      <Icon className="mt-0.5 size-3.5 shrink-0 text-foreground" aria-hidden />
      <span>{children}</span>
    </p>
  );
}
